import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { Importer } from '@feathr/blackbox';
import { Button } from '@feathr/components';
import { getImportDisplayName } from '@feathr/extender/utils/importDisplayName';
import { getImportFailureMessage } from '@feathr/extender/utils/importFailureMessage';

interface IImporterAlertMessageProps {
  importer: Importer;
  onRefresh: () => void;
  showReason?: boolean;
}

/** Each `Trans` sets `shouldUnescape`, or i18n and React both escape the name ("A &amp; B"). */
function ImporterAlertMessage({
  importer,
  onRefresh,
  showReason = false,
}: Readonly<IImporterAlertMessageProps>): JSX.Element | null {
  const { t } = useTranslation();
  const name = getImportDisplayName(importer, t);
  const refreshButton = (
    <>
      {' '}
      <Button onClick={onRefresh} type={'link'}>
        {t('Refresh partners')}
      </Button>
      .
    </>
  );

  switch (importer.get('state')) {
    case 'pending':

    case 'processing':
      return (
        <p>
          <Trans shouldUnescape={true} t={t}>
            Importing <em>{{ name }}</em> is in progress...
          </Trans>
        </p>
      );

    case 'complete':
      return (
        <p>
          <Trans shouldUnescape={true} t={t}>
            Importing <em>{{ name }}</em> has completed.
          </Trans>
          {refreshButton}
        </p>
      );

    case 'failed': {
      const failedSentence = (
        <Trans shouldUnescape={true} t={t}>
          Importing <em>{{ name }}</em> failed.
        </Trans>
      );
      if (!showReason) {
        return (
          <p>
            {failedSentence}
            {refreshButton}
          </p>
        );
      }
      return (
        <>
          <p>{failedSentence}</p>
          <p>
            {getImportFailureMessage(importer.get('failure_message'), t)}
            {refreshButton}
          </p>
        </>
      );
    }

    case 'draft':
      return null;
  }
}

export default observer(ImporterAlertMessage);
